import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '../components/ui/dialog';
import { Package, Plus, Laptop, Monitor, Cpu, User, Pencil, Trash2, UserPlus, Loader2, Undo2, Smartphone, Headphones, Shield, Activity, Wrench } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const typeIcons = { laptop: Laptop, monitor: Monitor, gpu_workstation: Cpu, phone: Smartphone, headset: Headphones };
const typeLabels = { laptop: 'Laptop', monitor: 'Monitor', gpu_workstation: 'GPU Workstation', phone: 'Phone', headset: 'Headset', keyboard: 'Keyboard', mouse: 'Mouse', server: 'Server', other: 'Other' };

const AssetsPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'hr';
  const [assets, setAssets] = useState([]);
  const [myAssets, setMyAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(isAdmin ? 'all' : 'my');
  const [typeFilter, setTypeFilter] = useState('all');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [editAsset, setEditAsset] = useState(null);
  const [assignAsset, setAssignAsset] = useState(null);
  const [assignForm, setAssignForm] = useState({ employee_id: '', assigned_date: '', notes: '' });
  const [newAsset, setNewAsset] = useState({ name: '', type: 'laptop', serial_number: '', purchase_date: '', purchase_cost: '', ram: '', cpu: '', model_number: '', allocation_date: '', warranty_expiry: '', warranty_provider: '', depreciation_rate: '20', health_status: 'healthy', last_maintenance: '', next_maintenance: '' });

  const fetchData = useCallback(async () => {
    try {
      const [assetsRes, myRes] = await Promise.all([
        isAdmin ? axios.get(`${API_URL}/api/assets`, { withCredentials: true }) : Promise.resolve({ data: [] }),
        axios.get(`${API_URL}/api/assets/my`, { withCredentials: true }),
      ]);
      setAssets(assetsRes.data);
      setMyAssets(myRes.data);
      if (isAdmin) {
        const empRes = await axios.get(`${API_URL}/api/employees`, { withCredentials: true });
        setEmployees(empRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch assets:', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/assets`, {
        ...newAsset,
        purchase_cost: newAsset.purchase_cost ? parseFloat(newAsset.purchase_cost) : null,
        depreciation_rate: newAsset.depreciation_rate ? parseFloat(newAsset.depreciation_rate) : null,
      }, { withCredentials: true });
      toast.success('Asset added with auto-generated serial number');
      setIsAddOpen(false);
      setNewAsset({ name: '', type: 'laptop', serial_number: '', purchase_date: '', purchase_cost: '', ram: '', cpu: '', model_number: '', allocation_date: '', warranty_expiry: '', warranty_provider: '', depreciation_rate: '20', health_status: 'healthy', last_maintenance: '', next_maintenance: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add'); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/api/assets/${editAsset.id}`, {
        name: editAsset.name, type: editAsset.type, serial_number: editAsset.serial_number,
        purchase_date: editAsset.purchase_date || null,
        purchase_cost: editAsset.purchase_cost ? parseFloat(editAsset.purchase_cost) : null,
        ram: editAsset.ram || null, cpu: editAsset.cpu || null,
        model_number: editAsset.model_number || null, allocation_date: editAsset.allocation_date || null,
        warranty_expiry: editAsset.warranty_expiry || null,
        warranty_provider: editAsset.warranty_provider || null,
        depreciation_rate: editAsset.depreciation_rate !== '' && editAsset.depreciation_rate != null ? parseFloat(editAsset.depreciation_rate) : null,
        health_status: editAsset.health_status || null,
        last_maintenance: editAsset.last_maintenance || null,
        next_maintenance: editAsset.next_maintenance || null,
        notes: editAsset.notes || null,
      }, { withCredentials: true });
      toast.success('Asset updated');
      setIsEditOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/api/assets/${assignAsset.id}/assign`, assignForm, { withCredentials: true });
      toast.success('Asset assigned');
      setIsAssignOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to assign'); }
  };

  const handleReturn = async (assetId) => {
    if (!window.confirm('Return this asset?')) return;
    try {
      await axios.put(`${API_URL}/api/assets/${assetId}/return`, {}, { withCredentials: true });
      toast.success('Asset returned');
      fetchData();
    } catch (err) { toast.error('Failed to return'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/assets/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchData();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const filteredAssets = typeFilter === 'all' ? assets : assets.filter(a => a.type === typeFilter);

  const getIcon = (type) => { const I = typeIcons[type] || Package; return <I className="w-5 h-5" />; };
  const getBadge = (status) => {
    if (status === 'assigned') return <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">Assigned</Badge>;
    if (status === 'available') return <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Available</Badge>;
    return <Badge variant="outline">{status}</Badge>;
  };
  const getHealthBadge = (h) => {
    if (!h) return null;
    const map = {
      healthy: { c: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', l: 'Healthy' },
      attention: { c: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', l: 'Needs Attention' },
      maintenance: { c: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400', l: 'In Maintenance' },
      retired: { c: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300', l: 'Retired' },
    };
    const m = map[h] || { c: 'bg-muted', l: h };
    return <Badge className={`${m.c} text-[10px] gap-1`}><Activity className="w-3 h-3" /> {m.l}</Badge>;
  };
  const isWarrantyExpiring = (date) => {
    if (!date) return false;
    const d = new Date(date);
    const now = new Date();
    const days = (d - now) / (1000 * 60 * 60 * 24);
    return days >= 0 && days <= 60;
  };
  const isWarrantyExpired = (date) => {
    if (!date) return false;
    return new Date(date) < new Date();
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="assets-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Package className="w-7 h-7 text-primary" /> Asset Management</h1>
          <p className="text-muted-foreground">Track and manage company assets</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setIsAddOpen(true)} className="gap-2" data-testid="add-asset-btn">
            <Plus className="w-4 h-4" /> Add Asset
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          {isAdmin && <TabsTrigger value="all" data-testid="tab-all-assets">All Assets ({assets.length})</TabsTrigger>}
          <TabsTrigger value="my" data-testid="tab-my-assets">My Assets ({myAssets.length})</TabsTrigger>
        </TabsList>

        {/* All Assets (Admin) */}
        {isAdmin && (
          <TabsContent value="all" className="mt-4">
            <div className="flex gap-2 mb-4 flex-wrap">
              {['all', 'laptop', 'monitor', 'gpu_workstation', 'phone', 'other'].map(t => (
                <Button key={t} variant={typeFilter === t ? 'default' : 'outline'} size="sm" onClick={() => setTypeFilter(t)} className="capitalize">
                  {t === 'all' ? 'All' : typeLabels[t] || t}
                </Button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredAssets.map((asset) => (
                <Card key={asset.id} data-testid={`asset-${asset.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3">
                      <div className="p-2.5 bg-muted rounded-lg">{getIcon(asset.type)}</div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm truncate">{asset.name}</h3>
                        <p className="text-xs font-mono text-primary">{asset.serial_number}</p>
                        <div className="flex items-center gap-2 mt-1.5">{getBadge(asset.status)}</div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        {asset.status === 'available' && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="Assign"
                            onClick={() => { setAssignAsset(asset); setAssignForm({ employee_id: '', assigned_date: new Date().toISOString().slice(0, 10), notes: '' }); setIsAssignOpen(true); }}
                            data-testid={`assign-asset-${asset.id}`}>
                            <UserPlus className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {asset.status === 'assigned' && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="Return" onClick={() => handleReturn(asset.id)} data-testid={`return-asset-${asset.id}`}>
                            <Undo2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditAsset({ ...asset }); setIsEditOpen(true); }} data-testid={`edit-asset-${asset.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(asset.id, asset.name)} data-testid={`delete-asset-${asset.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                    {asset.assigned_to_name && (
                      <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                        <User className="w-3.5 h-3.5" />
                        <span>Assigned to <span className="font-medium text-foreground">{asset.assigned_to_name}</span></span>
                      </div>
                    )}
                    {/* Lifecycle info */}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {getHealthBadge(asset.health_status)}
                      {asset.warranty_expiry && (
                        <Badge className={`text-[10px] gap-1 ${isWarrantyExpired(asset.warranty_expiry) ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : isWarrantyExpiring(asset.warranty_expiry) ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'}`}>
                          <Shield className="w-3 h-3" /> {isWarrantyExpired(asset.warranty_expiry) ? 'Warranty Expired' : `Warranty till ${asset.warranty_expiry}`}
                        </Badge>
                      )}
                      {asset.next_maintenance && (
                        <Badge variant="outline" className="text-[10px] gap-1"><Wrench className="w-3 h-3" /> Maint: {asset.next_maintenance}</Badge>
                      )}
                    </div>
                    {asset.purchase_cost && (
                      <div className="mt-2 font-mono text-xs text-muted-foreground space-y-0.5">
                        <p>Cost: ₹{asset.purchase_cost.toLocaleString()}</p>
                        {asset.current_value !== undefined && asset.current_value !== null && (
                          <p>Current Value: <span className="text-foreground">₹{Number(asset.current_value).toLocaleString()}</span> {asset.depreciation_rate ? `(@${asset.depreciation_rate}% dep.)` : ''}</p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              {filteredAssets.length === 0 && (
                <div className="col-span-full text-center py-16 text-muted-foreground">No assets found</div>
              )}
            </div>
          </TabsContent>
        )}

        {/* My Assets (Employee view - no cost) */}
        <TabsContent value="my" className="mt-4">
          {myAssets.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <Package className="w-12 h-12 text-muted-foreground/30 mb-3" />
                <p className="text-muted-foreground">No assets assigned to you</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myAssets.map((asset) => (
                <Card key={asset.id} data-testid={`my-asset-${asset.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3">
                      <div className="p-2.5 bg-primary/10 text-primary rounded-lg">{getIcon(asset.type)}</div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm">{asset.name}</h3>
                        <p className="text-xs font-mono text-primary mt-0.5">{asset.serial_number}</p>
                        <p className="text-xs text-muted-foreground mt-1 capitalize">{typeLabels[asset.type] || asset.type}</p>
                        {asset.assigned_date && (
                          <p className="text-xs text-muted-foreground">Since {asset.assigned_date}</p>
                        )}
                      </div>
                      <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 shrink-0">Assigned</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Asset Dialog */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Asset</DialogTitle>
            <DialogDescription>Serial number auto-generated based on type (e.g., LPT-0001)</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4">
            <div className="space-y-2">
              <Label>Asset Name</Label>
              <Input value={newAsset.name} onChange={(e) => setNewAsset({ ...newAsset, name: e.target.value })} required placeholder="MacBook Pro 16 inch" data-testid="new-asset-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={newAsset.type} onValueChange={(v) => setNewAsset({ ...newAsset, type: v })}>
                  <SelectTrigger data-testid="new-asset-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(typeLabels).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Custom Serial (optional)</Label>
                <Input value={newAsset.serial_number} onChange={(e) => setNewAsset({ ...newAsset, serial_number: e.target.value })} placeholder="Auto-generated" data-testid="new-asset-serial" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Purchase Date</Label>
                <Input type="date" value={newAsset.purchase_date} onChange={(e) => setNewAsset({ ...newAsset, purchase_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Cost (₹)</Label>
                <Input type="number" value={newAsset.purchase_cost} onChange={(e) => setNewAsset({ ...newAsset, purchase_cost: e.target.value })} placeholder="75000" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>RAM</Label>
                <Input value={newAsset.ram} onChange={(e) => setNewAsset({ ...newAsset, ram: e.target.value })} placeholder="16GB" data-testid="new-asset-ram" />
              </div>
              <div className="space-y-2">
                <Label>CPU</Label>
                <Input value={newAsset.cpu} onChange={(e) => setNewAsset({ ...newAsset, cpu: e.target.value })} placeholder="M3 Pro" data-testid="new-asset-cpu" />
              </div>
              <div className="space-y-2">
                <Label>Model Number</Label>
                <Input value={newAsset.model_number} onChange={(e) => setNewAsset({ ...newAsset, model_number: e.target.value })} placeholder="A2918" data-testid="new-asset-model" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Allocation Date</Label>
              <Input type="date" value={newAsset.allocation_date} onChange={(e) => setNewAsset({ ...newAsset, allocation_date: e.target.value })} data-testid="new-asset-alloc-date" />
            </div>
            {/* Lifecycle fields */}
            <div className="border-t pt-3 space-y-3">
              <p className="text-xs uppercase font-semibold tracking-wider text-muted-foreground flex items-center gap-1.5"><Shield className="w-3 h-3" /> Lifecycle</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Warranty Expiry</Label>
                  <Input type="date" value={newAsset.warranty_expiry} onChange={(e) => setNewAsset({ ...newAsset, warranty_expiry: e.target.value })} data-testid="new-asset-warranty-expiry" />
                </div>
                <div className="space-y-2">
                  <Label>Warranty Provider</Label>
                  <Input value={newAsset.warranty_provider} onChange={(e) => setNewAsset({ ...newAsset, warranty_provider: e.target.value })} placeholder="Apple, Dell..." data-testid="new-asset-warranty-provider" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Depreciation Rate (% / year)</Label>
                  <Input type="number" value={newAsset.depreciation_rate} onChange={(e) => setNewAsset({ ...newAsset, depreciation_rate: e.target.value })} placeholder="20" data-testid="new-asset-dep-rate" />
                </div>
                <div className="space-y-2">
                  <Label>Health Status</Label>
                  <Select value={newAsset.health_status} onValueChange={(v) => setNewAsset({ ...newAsset, health_status: v })}>
                    <SelectTrigger data-testid="new-asset-health"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="healthy">Healthy</SelectItem>
                      <SelectItem value="attention">Needs Attention</SelectItem>
                      <SelectItem value="maintenance">In Maintenance</SelectItem>
                      <SelectItem value="retired">Retired</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Last Maintenance</Label>
                  <Input type="date" value={newAsset.last_maintenance} onChange={(e) => setNewAsset({ ...newAsset, last_maintenance: e.target.value })} data-testid="new-asset-last-maint" />
                </div>
                <div className="space-y-2">
                  <Label>Next Maintenance</Label>
                  <Input type="date" value={newAsset.next_maintenance} onChange={(e) => setNewAsset({ ...newAsset, next_maintenance: e.target.value })} data-testid="new-asset-next-maint" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsAddOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="submit-asset-btn">Add Asset</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Asset Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Asset</DialogTitle></DialogHeader>
          {editAsset && (
            <form onSubmit={handleEdit} className="space-y-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input value={editAsset.name || ''} onChange={(e) => setEditAsset({ ...editAsset, name: e.target.value })} required data-testid="edit-asset-name" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Serial</Label>
                  <Input value={editAsset.serial_number || ''} onChange={(e) => setEditAsset({ ...editAsset, serial_number: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Cost (₹)</Label>
                  <Input type="number" value={editAsset.purchase_cost || ''} onChange={(e) => setEditAsset({ ...editAsset, purchase_cost: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>RAM</Label>
                  <Input value={editAsset.ram || ''} onChange={(e) => setEditAsset({ ...editAsset, ram: e.target.value })} placeholder="16GB" data-testid="edit-asset-ram" />
                </div>
                <div className="space-y-2">
                  <Label>CPU</Label>
                  <Input value={editAsset.cpu || ''} onChange={(e) => setEditAsset({ ...editAsset, cpu: e.target.value })} placeholder="M3 Pro" data-testid="edit-asset-cpu" />
                </div>
                <div className="space-y-2">
                  <Label>Model Number</Label>
                  <Input value={editAsset.model_number || ''} onChange={(e) => setEditAsset({ ...editAsset, model_number: e.target.value })} placeholder="A2918" data-testid="edit-asset-model" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Allocation Date</Label>
                <Input type="date" value={editAsset.allocation_date || ''} onChange={(e) => setEditAsset({ ...editAsset, allocation_date: e.target.value })} data-testid="edit-asset-alloc-date" />
              </div>
              {/* Lifecycle */}
              <div className="border-t pt-3 space-y-3">
                <p className="text-xs uppercase font-semibold tracking-wider text-muted-foreground flex items-center gap-1.5"><Shield className="w-3 h-3" /> Lifecycle</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Warranty Expiry</Label>
                    <Input type="date" value={editAsset.warranty_expiry || ''} onChange={(e) => setEditAsset({ ...editAsset, warranty_expiry: e.target.value })} data-testid="edit-asset-warranty-expiry" />
                  </div>
                  <div className="space-y-2">
                    <Label>Warranty Provider</Label>
                    <Input value={editAsset.warranty_provider || ''} onChange={(e) => setEditAsset({ ...editAsset, warranty_provider: e.target.value })} placeholder="Apple, Dell..." />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Depreciation Rate (% / year)</Label>
                    <Input type="number" value={editAsset.depreciation_rate ?? ''} onChange={(e) => setEditAsset({ ...editAsset, depreciation_rate: e.target.value })} placeholder="20" data-testid="edit-asset-dep-rate" />
                  </div>
                  <div className="space-y-2">
                    <Label>Health Status</Label>
                    <Select value={editAsset.health_status || 'healthy'} onValueChange={(v) => setEditAsset({ ...editAsset, health_status: v })}>
                      <SelectTrigger data-testid="edit-asset-health"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="healthy">Healthy</SelectItem>
                        <SelectItem value="attention">Needs Attention</SelectItem>
                        <SelectItem value="maintenance">In Maintenance</SelectItem>
                        <SelectItem value="retired">Retired</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Last Maintenance</Label>
                    <Input type="date" value={editAsset.last_maintenance || ''} onChange={(e) => setEditAsset({ ...editAsset, last_maintenance: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Next Maintenance</Label>
                    <Input type="date" value={editAsset.next_maintenance || ''} onChange={(e) => setEditAsset({ ...editAsset, next_maintenance: e.target.value })} />
                  </div>
                </div>
                {editAsset.current_value !== undefined && editAsset.current_value !== null && (
                  <div className="text-xs text-muted-foreground p-2 rounded bg-muted/50">Current depreciated value: <span className="font-semibold text-foreground font-mono">₹{Number(editAsset.current_value).toLocaleString()}</span></div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-asset-edit">Save</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Asset Dialog */}
      <Dialog open={isAssignOpen} onOpenChange={setIsAssignOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Asset</DialogTitle>
            <DialogDescription>Assign {assignAsset?.name} ({assignAsset?.serial_number}) to an employee</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAssign} className="space-y-4">
            <div className="space-y-2">
              <Label>Employee</Label>
              <Select value={assignForm.employee_id} onValueChange={(v) => setAssignForm({ ...assignForm, employee_id: v })}>
                <SelectTrigger data-testid="assign-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name} — {emp.employee_id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Assignment Date</Label>
              <Input type="date" value={assignForm.assigned_date} onChange={(e) => setAssignForm({ ...assignForm, assigned_date: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input value={assignForm.notes} onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })} placeholder="Configuration, condition notes..." />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsAssignOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={!assignForm.employee_id} data-testid="confirm-assign-btn">Assign</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetsPage;
